// A board on screen (docs/plans/boards.md B2/B3) — the phone's Board screen and the web's /board/:kind/:id read the
// same thing: the scope's board (shared/boards.js), its cards filtered, the columns, the card facts, and `move` (the
// drop). A drop is one group through the shared commands (moveCardWrites); a plain "→ Done" goes through the app's own
// completeTask so a running timer is banked and the after-photo / metric prompts still come up.
import { useCallback, useMemo } from 'react';
import { newId } from '@battleops/localstore';
import {
  boardForScope, boardTasks, boardColumns, filterCards, boardFilterOptions, noteCountsByTask, notesForCard, cardFacts,
  planCardMove, moveCardWrites, listDepartments, departmentsWithBoards,
} from '@battleops/shared';
import { useTasks } from '../context/TaskContext';
import { useProjects } from '../context/ProjectContext';
import { useDepartments } from '../context/DepartmentContext';
import { useNotes } from '../context/NoteContext';
import { useSections } from '../context/SectionContext';
import { useSession } from '../context/SessionContext';
import { useLocalCollection } from './useLocal';
import { commitWrites } from './commit';

const initials = (name = '') => String(name).trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() || '').join('') || '?';

/**
 * scope = { kind: 'department' | 'project' | 'task', id }; filter = { q, tags, assignee, projectId }.
 * → { found, board, columns, all, shown, options, facts(task), notesFor(taskId), person(uid), project(id), plan(task, col),
 *     move(taskId, columnId, note) → plan, title, departments (with boards on) }
 */
export function useBoard(scope, filter = {}) {
  const { tasks, completeTask, timer, lock, clockedIn } = useTasks();
  const { projects } = useProjects();
  const { rawDepartments, departmentMode } = useDepartments();
  const { notes } = useNotes();
  const { sections } = useSections();
  const { identity } = useSession() || {};
  const members = useLocalCollection('members');
  const me = identity?.uid || null;

  const found = useMemo(() => (scope?.kind && scope?.id ? boardForScope(scope, { departments: rawDepartments || [], projects, tasks }) : null), [scope?.kind, scope?.id, rawDepartments, projects, tasks]);
  const board = found?.board || null;
  const all = useMemo(() => (found ? boardTasks(scope, { tasks, projects, departments: rawDepartments || [], departmentMode }) : []), [found, scope?.kind, scope?.id, tasks, projects, rawDepartments, departmentMode]);
  const fkey = JSON.stringify(filter || {});
  const shown = useMemo(() => filterCards(all, filter, { me }), [all, fkey, me]);
  const columns = useMemo(() => (board ? boardColumns(board, shown, { order: scope?.kind === 'department' ? 'computed' : 'outline' }) : []), [board, shown, scope?.kind]);
  const options = useMemo(() => boardFilterOptions(all), [all]);
  const noteCounts = useMemo(() => noteCountsByTask(notes || []), [notes]);
  const facts = useCallback((t) => cardFacts(t, { tasks, noteCounts }), [tasks, noteCounts]);
  const notesFor = useCallback((taskId) => notesForCard(notes || [], taskId), [notes]);
  const byProject = useMemo(() => new Map(projects.map((p) => [p.id, p])), [projects]);
  const project = useCallback((id) => (id ? byProject.get(id) || null : null), [byProject]);
  const person = useCallback((uid) => {
    if (!uid) return null;
    const m = members.find((x) => (x.uid || x.id) === uid);
    const name = uid === me ? 'You' : m?.name || 'Teammate';
    return { uid, name, initials: initials(uid === me ? identity?.name || 'You' : name), agent: !!m?.agent };
  }, [members, me, identity?.name]);

  const title = useMemo(() => {
    if (!found) return '';
    const o = found.owner;
    return scope.kind === 'task' ? o.title : o.name;
  }, [found, scope?.kind]);

  const plan = useCallback((task, columnId) => planCardMove(task, board, columnId), [board]);

  // The drop. `note` = the hand-back / send-back line (the screens ask for it when plan.note says so).
  const move = useCallback(async (taskId, columnId, note = '') => {
    const task = tasks.find((t) => t.id === taskId);
    if (!task || !board) return null;
    const p = planCardMove(task, board, columnId);
    if (p.same) return p;
    if (p.steps.length === 1 && p.steps[0] === 'complete') { await completeTask(taskId); return p; }
    const ctx = { tasks, sections: sections || [], timer, lock, clock: clockedIn ? { ...clockedIn, active: true } : null, now: Date.now, newId, by: me ? { uid: me, name: identity?.name || null } : null };
    const r = moveCardWrites(ctx, { taskId, board, columnId, note, by: ctx.by });
    await commitWrites(r.writes);
    return r.plan;
  }, [tasks, board, sections, timer, lock, clockedIn, me, identity?.name, completeTask]);

  const departments = useMemo(() => departmentsWithBoards(rawDepartments || []), [rawDepartments]);
  const allDepartments = useMemo(() => listDepartments(rawDepartments || []), [rawDepartments]);

  return { found, board, columns, all, shown, options, facts, notesFor, person, project, plan, move, title, me, departments, allDepartments };
}
